<template>
  <div class="opentok-setup">
    <div class="d-flex flex-column align-items-center justify-content-center">
      <HMSpinner class="spinner" v-if="!stream && !error" />
      <p v-if="error" class="text-danger mt-4">
        {{ error }}
      </p>
    </div>
    <div
      id="video"
      class="d-flex flex-column align-items-center justify-content-center"
    >
      <video
        v-show="streamingProps.videoEnabled"
        ref="video"
        class="video mx-auto"
        prevent-right-click
        autoplay="true"
        playsinline="true"
        muted="true"
      ></video>
      <div v-if="!streamingProps.videoEnabled" class="empty-video">
        <i class="fal fa-video-slash h1 c-charcoal"></i>
      </div>
      <div class="smallest">
        <!-- debug info -->
        v29032021.001
      </div>
    </div>
    <OpenTokHWSetupForm
      v-if="stream || error"
      :stream="stream"
      :availableVideoDevices="availableVideoDevices"
      :availableAudioInputDevices="availableAudioInputDevices"
      :availableAudioOutputDevices="availableAudioOutputDevices"
    />
    <div class="form-group d-flex align-items-center justify-content-center">
      <div
        v-tooltip="{
          content: getSetupErrorText,
          delay: 100,
          placement: 'top',
          container: 'body',
          trigger: 'click hover',
        }"
        class="w-100"
      >
        <button
          :disabled="!stream || !setupComplete"
          class="btn btn-primary bg-coral c-white w-100"
          @click="$emit('close')"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import OT from '@opentok/client';
import OpenTokHWSetupForm from './OpenTokHWSetupForm';
import HMSpinner from '@/web/components/common/HMSpinner.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'OpenTokPreStreamSetup',
  components: {
    OpenTokHWSetupForm,
    HMSpinner,
  },
  props: {
    buttonText: {
      type: String,
    },
  },
  computed: {
    ...mapGetters({
      streamingProps: 'setting/streamingProps',
    }),
    selectedVideoDeviceExists() {
      if (!this.availableVideoDevices) return false;
      return this.availableVideoDevices.some(
        device =>
          device.deviceId === this.streamingProps?.selectedVideoDevice.deviceId
      );
    },
    selectedAudioInputDeviceExists() {
      if (!this.availableAudioInputDevices) return false;
      return this.availableAudioInputDevices.some(
        device =>
          device.deviceId ===
          this.streamingProps?.selectedAudioInputDevice.deviceId
      );
    },
    isVideoSetupCorrect() {
      const props = this.streamingProps;
      return (
        props?.videoEnabled &&
        props?.selectedVideoDevice &&
        this.selectedVideoDeviceExists
      );
    },
    isAudioSetupCorrect() {
      const props = this.streamingProps;
      return (
        props?.audioInputEnabled &&
        props?.selectedAudioInputDevice &&
        this.selectedAudioInputDeviceExists
      );
    },
    setupComplete() {
      if (!this.streamingProps) return false;
      if (!this.isVideoSetupCorrect) return false;
      if (!this.isAudioSetupCorrect) return false;
      return true;
    },
    getSetupErrorText() {
      if (!this.streamingProps) return null;
      if (!this.isVideoSetupCorrect) {
        return this.$t('EVENTS.TEXT.SELECT_YOUR_VIDEO_DEVICE');
      }
      if (!this.isAudioSetupCorrect) {
        return this.$t('EVENTS.TEXT.SELECT_YOUR_AUDIO_DEVICE');
      }
      return null;
    },
  },
  data() {
    return {
      stream: null,
      error: null,
      availableVideoDevices: [],
      availableAudioInputDevices: [],
      availableAudioOutputDevices: [],
    };
  },
  methods: {
    videoDeviceSelected(videoDevice) {
      this.$store.dispatch('setting/setStreamingProps', {
        selectedVideoDevice: videoDevice,
      });
      this.updateStream();
    },
    audioInputDeviceSelected(audioInputDevice) {
      this.$store.dispatch('setting/setStreamingProps', {
        selectedAudioInputDevice: audioInputDevice,
      });
      this.updateStream();
    },
    audioOutputDeviceSelected(audioOutputDevice) {
      this.$store.dispatch('setting/setStreamingProps', {
        selectedAudioOutputDevice: audioOutputDevice,
      });
    },
    videoEnabled(enabled) {
      this.$store.dispatch('setting/setStreamingProps', {
        videoEnabled: enabled,
      });
      this.updateStream();
    },
    audioEnabled(enabled) {
      this.$store.dispatch('setting/setStreamingProps', {
        audioInputEnabled: enabled,
      });
      this.updateStream();
    },
    useContainMode(useContainMode) {
      this.$store.dispatch('setting/setStreamingProps', {
        useContainMode: useContainMode,
      });
    },
    advancedAudioProcessing(advancedAudioProcessing) {
      this.$store.dispatch('setting/setStreamingProps', {
        advancedAudioProcessing: advancedAudioProcessing,
      });
    },
    shortenDeviceLabel(label) {
      return label.replace(/ \(\w+:\w+\) */g, '');
    },
    mapDevice(device) {
      return {
        label: this.shortenDeviceLabel(device.label),
        deviceId: device.deviceId,
      };
    },
    async getDeviceInfo() {
      await new Promise((resolve, reject) => {
        OT.getDevices((error, devices) => {
          if (error) {
            console.error('Failed to enumerate media devices: ', error);
            this.error = `${error}`;
            reject(error);
          } else {
            console.log('Got devices = ', devices);
            this.availableVideoDevices = devices
              .filter(device => device.kind?.toLowerCase() === 'videoinput')
              .map(this.mapDevice);
            this.availableAudioInputDevices = devices
              .filter(device => device.kind?.toLowerCase() === 'audioinput')
              .map(this.mapDevice);
            this.availableAudioOutputDevices = devices
              .filter(device => device.kind?.toLowerCase() === 'audiooutput')
              .map(this.mapDevice);
            resolve();
          }
        });
      });
    },
    async start() {
      const constraints = { audio: true, video: true };
      await navigator.mediaDevices.getUserMedia(constraints);

      await this.getDeviceInfo();
      this.updateStream();
    },
    async gotStream(stream) {
      const videoElement = this.$refs['video'];
      //await this.getDeviceInfo();
      this.stream = stream;
      videoElement.srcObject = this.stream;
    },
    updateStream: _.debounce(function () {
      // because setup form emits input events, this gets called too many times
      // and many streams are created, so we debounce this a bit
      this._updateStream();
    }, 700),
    _updateStream() {
      this.error = null;
      if (this.stream) {
        this.stream.getTracks().forEach(track => {
          track.stop();
        });
      }
      const constraints = {
        video: { facingMode: 'user' }, // by default request the user facing camera
        audio: true,
      };
      const selectedVideoDevice = _.get(
        this.streamingProps.selectedVideoDevice,
        'deviceId',
        false
      );
      const selectedAudioDevice = _.get(
        this.streamingProps.selectedAudioInputDevice,
        'deviceId',
        false
      );
      if (selectedVideoDevice) {
        constraints.video = { deviceId: selectedVideoDevice };
      }
      if (selectedAudioDevice) {
        constraints.audio = { deviceId: selectedAudioDevice };
      }
      console.log('stream constraints: ', constraints);
      try {
        navigator.mediaDevices
          .getUserMedia(constraints)
          .then(this.gotStream)
          .catch(error => {
            this.error = `${error}`;
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Failed to access camera/microphone',
              text: `Error: ${error}`,
            });
          });
      } catch (error) {
        this.error = `${error}`;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to access camera/microphone',
          text: `getUserMedia failed: ${error}`,
        });
      }
    },
  },
  async mounted() {
    await this.start();
  },
  destroyed() {
    if (this.stream) {
      this.stream.getTracks().forEach(function (track) {
        track.stop();
      });
    }
  },
  watch: {
    'streamingProps.videoEnabled': {
      handler(videoEnabled) {
        this.updateStream();
      },
      immediate: false,
    },
    streamingProps: {
      handler(streamingProps) {
        this.updateStream();
      },
      immediate: false,
    },
  },
};
</script>

<style lang="scss">
@import '@/web/styles/components-variables.scss';

.opentok-setup {
  width: auto;
  max-width: 100%;
  margin-top: 30px;

  #video {
    max-width: 100%;
    max-height: 250px;

    .video {
      max-width: 100%;
      height: 250px;
      transform: scale(-1, 1);
      transform-origin: 50% 50%;
    }

    .empty-video {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $white;
      width: 100%;
      height: 350px;
      max-height: 100%;
    }
  }
}
</style>
